/**
 * Shoppable Reels — CellX self-hosted ReelUp clone
 * Vanilla JS, no bundler. IIFE to avoid global namespace pollution.
 */
(function () {
  "use strict";

  var CACHE_KEY = "sr_videos_v1";
  var CACHE_TTL = 60 * 1000; // 1 minute

  // ---------------------------------------------------------------------------
  // Session ID (anonymous fingerprint for analytics)
  // ---------------------------------------------------------------------------
  function getSessionId() {
    try {
      var sid = sessionStorage.getItem("sr_sid");
      if (!sid) {
        sid =
          Math.random().toString(36).slice(2) + Date.now().toString(36);
        sessionStorage.setItem("sr_sid", sid);
      }
      return sid;
    } catch (_) {
      return "anon";
    }
  }

  // ---------------------------------------------------------------------------
  // Fetch with sessionStorage cache
  // ---------------------------------------------------------------------------
  function fetchVideos(apiBase) {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (raw) {
        var cached = JSON.parse(raw);
        if (Date.now() - cached.ts < CACHE_TTL) {
          return Promise.resolve(cached.data);
        }
      }
    } catch (_) {}

    return fetch(apiBase + "/api/shoppable-reels")
      .then(function (r) {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.json();
      })
      .then(function (json) {
        try {
          sessionStorage.setItem(
            CACHE_KEY,
            JSON.stringify({ ts: Date.now(), data: json.videos })
          );
        } catch (_) {}
        return json.videos;
      });
  }

  // ---------------------------------------------------------------------------
  // Analytics tracking (fire-and-forget)
  // ---------------------------------------------------------------------------
  function trackEvent(apiBase, videoId, tagId, eventType) {
    var sid = getSessionId();
    fetch(apiBase + "/api/shoppable-reels/track", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        video_id: videoId,
        tag_id: tagId || null,
        event_type: eventType,
        session_id: sid,
      }),
      keepalive: true,
    }).catch(function () {});
  }

  // ---------------------------------------------------------------------------
  // Add to cart via Shopify AJAX API
  // ---------------------------------------------------------------------------
  function addToCart(variantId, onSuccess) {
    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    })
      .then(function (r) {
        if (!r.ok) throw new Error("Cart error");
        return r.json();
      })
      .then(function () {
        // Trigger Shopify cart drawer/bubble update
        document.dispatchEvent(new CustomEvent("cart:updated"));
        document.dispatchEvent(
          new CustomEvent("product:added-to-cart", {
            detail: { variantId: variantId },
          })
        );
        if (typeof onSuccess === "function") onSuccess();
      })
      .catch(function (err) {
        console.error("[ShoppableReels] addToCart error:", err);
      });
  }

  // ---------------------------------------------------------------------------
  // Toast notification
  // ---------------------------------------------------------------------------
  function showToast(message) {
    var existing = document.getElementById("sr-toast-global");
    if (!existing) {
      var toast = document.createElement("div");
      toast.id = "sr-toast-global";
      toast.className = "sr-toast";
      document.body.appendChild(toast);
      existing = toast;
    }
    existing.textContent = message;
    existing.classList.add("is-visible");
    setTimeout(function () {
      existing.classList.remove("is-visible");
    }, 2800);
  }

  // ---------------------------------------------------------------------------
  // WhatsApp URL builder
  // ---------------------------------------------------------------------------
  function buildWhatsAppUrl(tag, whatsapp) {
    var msg = encodeURIComponent(
      "Hola, me interesa: " +
        tag.product_title +
        " ($" +
        Number(tag.price_mxn).toLocaleString("es-MX") +
        " MXN) — vi el reel en cellx.com.mx"
    );
    return "https://wa.me/" + whatsapp + "?text=" + msg;
  }

  // ---------------------------------------------------------------------------
  // Create a product card element
  // ---------------------------------------------------------------------------
  function createProductCard(tag, videoId, whatsapp, apiBase) {
    var card = document.createElement("div");
    card.className = "sr-product-card";

    // Image
    if (tag.product_image_url) {
      var img = document.createElement("img");
      img.className = "sr-product-card__img";
      img.src = tag.product_image_url;
      img.alt = tag.product_title;
      img.loading = "lazy";
      card.appendChild(img);
    } else {
      var placeholder = document.createElement("div");
      placeholder.className = "sr-product-card__img--placeholder";
      card.appendChild(placeholder);
    }

    // Body
    var body = document.createElement("div");
    body.className = "sr-product-card__body";

    var title = document.createElement("p");
    title.className = "sr-product-card__title";
    title.textContent = tag.product_title;
    body.appendChild(title);

    var price = document.createElement("p");
    price.className = "sr-product-card__price";
    price.textContent =
      "$" + Number(tag.price_mxn).toLocaleString("es-MX") + " MXN";
    body.appendChild(price);

    var actions = document.createElement("div");
    actions.className = "sr-product-card__actions";

    // Add to cart
    var btnCart = document.createElement("button");
    btnCart.className = "sr-btn-cart";
    btnCart.textContent = "Añadir al carrito";
    btnCart.addEventListener("click", function () {
      btnCart.disabled = true;
      btnCart.textContent = "Agregando...";
      trackEvent(apiBase, videoId, tag.id, "add_to_cart");
      addToCart(tag.shopify_variant_id, function () {
        btnCart.textContent = "¡Agregado!";
        showToast("Agregado al carrito");
        setTimeout(function () {
          btnCart.disabled = false;
          btnCart.textContent = "Añadir al carrito";
        }, 2500);
      });
    });
    actions.appendChild(btnCart);

    // WhatsApp
    if (whatsapp) {
      var btnWa = document.createElement("a");
      btnWa.className = "sr-btn-wa";
      btnWa.href = buildWhatsAppUrl(tag, whatsapp);
      btnWa.target = "_blank";
      btnWa.rel = "noopener noreferrer";
      btnWa.setAttribute("aria-label", "Pedir vía WhatsApp");
      btnWa.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.016.499 3.919 1.374 5.592L0 24l6.562-1.353A11.95 11.95 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.814 9.814 0 0 1-5.013-1.375l-.36-.214-3.895.803.826-3.792-.234-.38A9.818 9.818 0 0 1 2.182 12C2.182 6.574 6.574 2.182 12 2.182S21.818 6.574 21.818 12 17.426 21.818 12 21.818z"/></svg>';
      btnWa.addEventListener("click", function () {
        trackEvent(apiBase, videoId, tag.id, "whatsapp");
      });
      actions.appendChild(btnWa);
    }

    body.appendChild(actions);
    card.appendChild(body);
    return card;
  }

  // ---------------------------------------------------------------------------
  // Instagram embed
  // ---------------------------------------------------------------------------
  var instagramScriptLoaded = false;

  function renderInstagramEmbed(container, instagramUrl) {
    container.innerHTML =
      '<blockquote class="instagram-media"' +
      ' data-instgrm-permalink="' +
      instagramUrl +
      '"' +
      ' data-instgrm-version="14"' +
      ' style="width:100%;min-width:260px;max-width:320px;">' +
      "</blockquote>";

    if (window.instgrm && window.instgrm.Embeds) {
      window.instgrm.Embeds.process();
    } else if (!instagramScriptLoaded) {
      instagramScriptLoaded = true;
      var s = document.createElement("script");
      s.id = "instagram-embed-js";
      s.src = "//www.instagram.com/embed.js";
      s.async = true;
      document.body.appendChild(s);
    }
  }

  // ---------------------------------------------------------------------------
  // Modal
  // ---------------------------------------------------------------------------
  function openModal(sectionId, video, whatsapp, apiBase) {
    var modal = document.getElementById("sr-modal-" + sectionId);
    var embedCol = document.getElementById("sr-embed-" + sectionId);
    var productsCol = document.getElementById("sr-products-" + sectionId);
    if (!modal || !embedCol || !productsCol) return;

    // Render embed
    renderInstagramEmbed(embedCol, video.instagram_url);

    // Render products
    productsCol.innerHTML = "";
    var heading = document.createElement("p");
    heading.className = "sr-products-title";
    heading.textContent = "Productos en este reel";
    productsCol.appendChild(heading);

    if (video.shoppable_video_tags && video.shoppable_video_tags.length > 0) {
      video.shoppable_video_tags.forEach(function (tag) {
        productsCol.appendChild(
          createProductCard(tag, video.id, whatsapp, apiBase)
        );
      });
    } else {
      var empty = document.createElement("p");
      empty.style.cssText = "color:#888;font-size:13px;";
      empty.textContent = "Sin productos etiquetados.";
      productsCol.appendChild(empty);
    }

    modal.classList.add("is-open");
    document.body.style.overflow = "hidden";

    // Track view
    trackEvent(apiBase, video.id, null, "view");
  }

  function closeModal(sectionId) {
    var modal = document.getElementById("sr-modal-" + sectionId);
    if (!modal) return;
    modal.classList.remove("is-open");
    document.body.style.overflow = "";
    // Clear embed to stop video
    var embedCol = document.getElementById("sr-embed-" + sectionId);
    if (embedCol) embedCol.innerHTML = "";
  }

  // ---------------------------------------------------------------------------
  // Create a video card element
  // ---------------------------------------------------------------------------
  function createCard(video, sectionId, whatsapp, apiBase) {
    var card = document.createElement("div");
    card.className = "sr-card";
    card.setAttribute("role", "button");
    card.setAttribute("tabindex", "0");
    card.setAttribute("aria-label", video.title);

    // Thumbnail
    var img = document.createElement("img");
    img.className = "sr-thumb";
    img.src = video.thumbnail_url;
    img.alt = video.title;
    img.loading = "lazy";
    card.appendChild(img);

    // Play icon overlay
    var playIcon = document.createElement("div");
    playIcon.className = "sr-play-icon";
    playIcon.innerHTML =
      '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><polygon points="5,3 19,12 5,21"/></svg>';
    card.appendChild(playIcon);

    // Tag dots overlay
    if (video.shoppable_video_tags && video.shoppable_video_tags.length > 0) {
      var overlay = document.createElement("div");
      overlay.className = "sr-dots-overlay";
      video.shoppable_video_tags.forEach(function (tag) {
        var dot = document.createElement("button");
        dot.className = "sr-dot";
        dot.style.left = tag.x_pct + "%";
        dot.style.top = tag.y_pct + "%";
        dot.setAttribute("aria-label", tag.product_title);
        dot.setAttribute("type", "button");
        dot.addEventListener("click", function (e) {
          e.stopPropagation();
          trackEvent(apiBase, video.id, tag.id, "tag_click");
          openModal(sectionId, video, whatsapp, apiBase);
        });
        overlay.appendChild(dot);
      });
      card.appendChild(overlay);
    }

    // Card click → open modal
    card.addEventListener("click", function () {
      openModal(sectionId, video, whatsapp, apiBase);
    });

    card.addEventListener("keydown", function (e) {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openModal(sectionId, video, whatsapp, apiBase);
      }
    });

    return card;
  }

  // ---------------------------------------------------------------------------
  // Main init per section
  // ---------------------------------------------------------------------------
  function init(sectionEl) {
    var sectionId = sectionEl.dataset.sectionId;
    var apiBase = (sectionEl.dataset.api || "").replace(/\/$/, "");
    var whatsapp = sectionEl.dataset.whatsapp || "";
    var colsDesktop = parseInt(sectionEl.dataset.colsDesktop, 10) || 4;

    if (!apiBase) return;

    var skeletonGrid = document.getElementById("sr-grid-" + sectionId);
    var realGrid = document.getElementById("sr-real-grid-" + sectionId);
    var modal = document.getElementById("sr-modal-" + sectionId);

    if (!realGrid) return;

    // Apply desktop column count from schema setting
    realGrid.style.setProperty("--sr-cols", String(colsDesktop));

    // Modal close handlers
    if (modal) {
      var backdrop = modal.querySelector(".sr-modal__backdrop");
      var closeBtn = modal.querySelector(".sr-modal__close");
      if (backdrop) {
        backdrop.addEventListener("click", function () {
          closeModal(sectionId);
        });
      }
      if (closeBtn) {
        closeBtn.addEventListener("click", function () {
          closeModal(sectionId);
        });
      }
      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape") closeModal(sectionId);
      });
    }

    // Fetch and render
    fetchVideos(apiBase)
      .then(function (videos) {
        if (!videos || videos.length === 0) {
          if (skeletonGrid) skeletonGrid.style.display = "none";
          // Hiding only the skeleton left the SECTION standing: a full-width black band with
          // "Reels de Instagram / Como lo usamos" and nothing underneath it. The API currently
          // returns {"videos":[]}, so that empty band was rendering on the live homepage.
          // With no content there is nothing to introduce, so the whole section goes away —
          // and comes back on its own the moment the API returns a video.
          var wrapper = sectionEl.closest(".shopify-section") || sectionEl;
          wrapper.style.display = "none";
          return;
        }

        videos.forEach(function (video) {
          realGrid.appendChild(
            createCard(video, sectionId, whatsapp, apiBase)
          );
        });

        // Swap skeleton → real grid
        if (skeletonGrid) skeletonGrid.style.display = "none";
        realGrid.classList.remove("sr-grid--hidden");
        realGrid.style.display = "";

        // Swiper for mobile (Swiper is already loaded in theme)
        if (window.Swiper && window.innerWidth < 768) {
          realGrid.classList.add("swiper-wrapper");
          realGrid.querySelectorAll(".sr-card").forEach(function (c) {
            c.classList.add("swiper-slide");
          });
          var wrapper = document.createElement("div");
          wrapper.className = "swiper";
          realGrid.parentNode.insertBefore(wrapper, realGrid);
          wrapper.appendChild(realGrid);
          new window.Swiper(wrapper, {
            slidesPerView: 1.25,
            spaceBetween: 10,
            freeMode: true,
            breakpoints: {
              480: { slidesPerView: 2.2 },
            },
          });
        }
      })
      .catch(function (err) {
        console.error("[ShoppableReels] fetch error:", err);
        if (skeletonGrid) skeletonGrid.style.display = "none";
      });
  }

  // ---------------------------------------------------------------------------
  // Bootstrap: find all shoppable-reels sections on the page
  // ---------------------------------------------------------------------------
  function bootstrap() {
    document
      .querySelectorAll(".sr-section[data-section-id]")
      .forEach(function (el) {
        if (el.dataset.srInit) return;
        el.dataset.srInit = "1";
        init(el);
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootstrap);
  } else {
    bootstrap();
  }
})();
